import { FC } from 'react';

import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui/Button/Button';
import { useEditTranscript } from '../../hooks/useEditTranscript';
import { TranscriptReviewEditStep } from '../add/TranscriptReviewEditStep';
import type { TranscriptBreakdown } from '@new/features/transcripts/shared/types';

interface EditTranscriptModalProps {
  transcript: TranscriptBreakdown;
  onClose: () => void;
  onSaved?: () => void;
}

const FIELD_STYLES =
  '[&_[data-slot=select]]:h-[48px]! [&_[data-slot=select]]:rounded-[8px]! [&_[data-slot=select]]:border-[1.5px]! [&_[data-slot=select]]:border-solid! [&_[data-slot=select]]:border-[#E1E7EA]! [&_[data-slot=select]]:bg-[#FCFDFD]! [&_input[data-slot=input]]:h-[48px]! [&_input[data-slot=input]]:md:h-[48px]! [&_input[data-slot=input]]:xl:h-[48px]! [&_input[data-slot=input]]:rounded-[8px]! [&_input[data-slot=input]]:border-[1.5px]! [&_input[data-slot=input]]:border-solid! [&_input[data-slot=input]]:border-[#E1E7EA]! [&_input[data-slot=input]]:bg-[#FCFDFD]!';

export const EditTranscriptModal: FC<EditTranscriptModalProps> = ({
  transcript,
  onClose,
  onSaved,
}) => {
  const edit = useEditTranscript(transcript, { onDone: onSaved });

  return (
    <Modal
      isOpen
      onClose={onClose}
      dismissable={false}
      title={edit.step === 'success' ? 'Transcript updated' : 'Edit transcript'}
      contentClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
      className={`h-[80vh] max-h-[80vh] ${
        edit.step === 'success' ? 'max-w-lg!' : 'max-w-5xl! md:max-w-5xl! lg:max-w-5xl!'
      } ${FIELD_STYLES}`}
    >
      <div className="flex h-full flex-col gap-6 overflow-hidden">
        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {edit.step === 'success' ? (
            <p className="[font-size:var(--fs-p2)]! font-semibold text-(--neutral-black-NB1000)!">
              Your transcript was updated.
            </p>
          ) : (
            <TranscriptReviewEditStep add={edit} />
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-(--neutral-black-NB100) pt-4">
          {edit.step === 'success' ? (
            <div className="ml-auto">
              <Button variant="primary" onClick={onClose} text="Done" />
            </div>
          ) : (
            <>
              <Button variant="neutral" onClick={onClose} text="Cancel" />
              <Button
                variant="primary"
                disabled={!edit.canSave || edit.isSaving}
                onClick={edit.save}
                text={edit.isSaving ? 'Saving…' : 'Save changes'}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};
